import React, { useEffect, useState, useCallback, useRef } from "react";
import Sidebar from "../components/common/Sidebar";
import { getCadCams, getMediaUrl } from "../services/catalogueService";
import bulletIcon from "../assets/images/title-bg3.png";
import "./CadCam.css";

export default function CadCam() {
  const [cadCams, setCadCams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [failedImages, setFailedImages] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgDimensions, setImgDimensions] = useState({ width: 250, height: 250 });
  const [containerReady, setContainerReady] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const resizeTimerRef = useRef(null);
  const slideshowTimerRef = useRef(null);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setImgLoaded(false);
    setContainerReady(false);
    setIsClosing(false);
    setIsPlaying(true);
    setImgDimensions({ width: 250, height: 250 });
    setLightboxOpen(true);
    requestAnimationFrame(() => setOverlayVisible(true));
  };

  const closeLightbox = () => {
    setIsClosing(true);
    setOverlayVisible(false);
    setIsPlaying(false);
    setTimeout(() => {
      setLightboxOpen(false);
      setIsClosing(false);
      setImgLoaded(false);
      setContainerReady(false);
    }, 200);
  };

  const navigateLightbox = useCallback((newIndex) => {
    setImgLoaded(false);
    setContainerReady(false);
    setLightboxIndex(newIndex);
  }, []);

  const nextLightboxImage = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!cadCams.length) return;
    setImgLoaded(false);
    setContainerReady(false);
    setLightboxIndex((prev) => (prev + 1) % cadCams.length);
  }, [cadCams.length]);

  const prevLightboxImage = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!cadCams.length) return;
    setIsPlaying(false);
    setImgLoaded(false);
    setContainerReady(false);
    setLightboxIndex((prev) => (prev === 0 ? cadCams.length - 1 : prev - 1));
  };

  const togglePlay = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsPlaying((prev) => !prev);
  };

  const handleLightboxImgLoad = useCallback((e) => {
    const img = e.target;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    const maxW = Math.min(window.innerWidth * 0.72, 680);
    const maxH = window.innerHeight - 170;
    let w = natW;
    let h = natH;
    if (w > maxW) { h = h * (maxW / w); w = maxW; }
    if (h > maxH) { w = w * (maxH / h); h = maxH; }
    setImgDimensions({ width: Math.round(w), height: Math.round(h) });
    clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      setContainerReady(true);
      setTimeout(() => setImgLoaded(true), 1000);
    }, 100);
  }, []);

  // Slideshow Effect
  useEffect(() => {
    if (lightboxOpen && isPlaying && imgLoaded) {
      clearTimeout(slideshowTimerRef.current);
      slideshowTimerRef.current = setTimeout(() => {
        nextLightboxImage();
      }, 6000);
    }
    return () => clearTimeout(slideshowTimerRef.current);
  }, [lightboxOpen, isPlaying, imgLoaded, lightboxIndex, nextLightboxImage]);

  const resolveImagePath = (item) => {
    if (!item || typeof item !== "object") {
      return "";
    }

    return (
      item.imageUrl ||
      item.OriginalPhoto ||
      item.CanvasPhoto ||
      item.ThumbPhoto ||
      item.image ||
      item.imagePath ||
      ""
    );
  };

  const resolveTitle = (item) => item?.title || item?.Name || "CAD-CAM Model";

  useEffect(() => {
    const fetchCadCams = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getCadCams();
        const activeCadCams = data
          .filter((item) => item?.isActive ?? item?.IsActive ?? true)
          .sort(
            (a, b) =>
              new Date(b.createdAt || 0) - new Date(a.createdAt || 0) ||
              Number(b.sequence || b.Id || 0) - Number(a.sequence || a.Id || 0)
          );

        setCadCams(activeCadCams);
      } catch (err) {
        console.error("Failed to load CAD/CAM items:", err);
        setError("Unable to load CAD/CAM items right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchCadCams();
  }, []);

  return (
    <>
      <Sidebar showNewsFooter={false} />
      <main className="content-area cadcam-page">
        <div className="catalogue-header">
          <h2>CAD-CAM Model</h2>
        </div>

        {loading ? <p className="catalogue-status">Loading CAD/CAM items...</p> : null}
        {error ? <p className="catalogue-status error">{error}</p> : null}

        {!loading && !error ? (
          cadCams.length > 0 ? (
            <div className="cadcam-gallery-shell">
              <div className="product-grid">
                {cadCams.map((item, index) => {
                  const imagePath = resolveImagePath(item);
                  const imageSrc = getMediaUrl(imagePath);
                  const imageKey = item._id || imageSrc;
                  const title = resolveTitle(item);

                  return (
                    <button
                      type="button"
                      className="product-card cadcam-card"
                      key={item._id || title}
                      onClick={() => openLightbox(index)}
                      aria-label={`Open ${title}`}
                    >
                      {imageSrc && !failedImages[imageKey] ? (
                        <span className="cadcam-thumb-frame">
                          <img
                            className="product-image cadcam-thumb"
                            src={imageSrc}
                            alt={title}
                            onError={() =>
                              setFailedImages((prev) => ({
                                ...prev,
                                [imageKey]: true,
                               }))
                            }
                          />
                        </span>
                      ) : (
                        <div className="product-image-placeholder cadcam-thumb-frame">
                          <span>No Image</span>
                        </div>
                      )}

                      <div className="product-info">
                        <h4 className="product-name">{title}</h4>
                      </div>
                    </button>
                  );
                })}
              </div>


            </div>
          ) : (
            <p className="catalogue-status">No CAD/CAM items available yet.</p>
          )
        ) : null}

        {lightboxOpen && cadCams.length > 0 && (
          <>
            <div
              className={`vlb-overlay ${overlayVisible && !isClosing ? 'vlb-overlay--visible' : ''}`}
              onClick={closeLightbox}
              aria-hidden="true"
            />
            <div
              className={`vlb-lightbox ${overlayVisible && !isClosing ? 'vlb-lightbox--visible' : ''}`}
              onClick={closeLightbox}
            >
              <div className="vlb-stage" onClick={(e) => e.stopPropagation()}>
                <div
                  className="vlb-outer-container"
                  style={{
                    width: imgDimensions.width + 4,
                    height: imgDimensions.height + 4,
                  }}
                >
                  <div className="vlb-image-container">
                    {!imgLoaded && (
                      <div className="vlb-loading">
                        <div className="vlb-spinner" />
                      </div>
                    )}
                    <button className="vlb-hover-prev" onClick={prevLightboxImage} aria-label="Previous">prev</button>
                    <button className="vlb-hover-next" onClick={nextLightboxImage} aria-label="Next">next</button>
                    <img
                      className={`vlb-img ${imgLoaded ? 'vlb-img--visible' : ''}`}
                      src={getMediaUrl(resolveImagePath(cadCams[lightboxIndex]))}
                      alt={resolveTitle(cadCams[lightboxIndex])}
                      onLoad={handleLightboxImgLoad}
                      key={`lb-${lightboxIndex}`}
                    />
                  </div>
                </div>
                <div className={`vlb-data ${imgLoaded ? 'vlb-data--visible' : ''}`}>
                  <div className="vlb-data-inner">
                    <div className="vlb-info">
                      <div className="vlb-caption">{resolveTitle(cadCams[lightboxIndex])}</div>
                      <div className="vlb-meta">
                        <div className="vlb-counter">{lightboxIndex + 1} / {cadCams.length}</div>
                        <div className="vlb-controls">
                          <button className="vlb-ctrl-btn vlb-ctrl-prev" onClick={prevLightboxImage} aria-label="Previous">prev</button>
                          <button 
                            className={`vlb-ctrl-btn vlb-ctrl-play ${!isPlaying ? 'vlb-ctrl-paused' : ''}`} 
                            onClick={togglePlay} 
                            style={{ backgroundPosition: isPlaying ? '-48px bottom' : '-24px bottom' }}
                            aria-label={isPlaying ? "Pause" : "Play"}
                          >
                            {isPlaying ? "pause" : "play"}
                          </button>
                          <button className="vlb-ctrl-btn vlb-ctrl-next" onClick={nextLightboxImage} aria-label="Next">next</button>
                        </div>
                      </div>
                    </div>
                    <button className="vlb-close" onClick={closeLightbox} aria-label="Close">✕</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </>
  );
}
